/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    AfterContentChecked,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DoCheck,
    ElementRef,
    EventEmitter,
    Injector,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    SimpleChanges,
    SkipSelf,
    ViewChild,
} from '@angular/core';
import { DigitizePointService } from '../../../map/command/digitize-point/digitize-point.service';
import { isolatedReportFactory, TypedReportDirective } from '../typed-report.directive';
import { LoggerService } from '../../../shared/services/logger.service';
import { defaultIfEmpty, distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';
import { Actions, Select, Store } from '@ngxs/store';
import { IsolatedReportService } from '../../services/isolatedReport.service';
import { ReportService } from '../../services/report.service';
import { Observable } from 'rxjs';
import { ReportState } from '../../state/report.state';
import { isEqual, throttle } from 'lodash-es';
import { LaunchStreetView } from '../../../map/state/active-map.actions';
import {
    ParcelDisplayAddress,
    ParcelDisplayOptions,
    ReportLaunchOptions,
    ReportSubtypeValue,
    StreetViewMetadata,
} from '../../state/report.model';
import { LaunchStreetViewPayload } from '../../../map/state/active-map.model';
import { ResizeObserver } from '@juggle/resize-observer';
import { ClearParcelDisplayOptions, SetParcelImageSize, SetParcelImageUrl } from '../../state/report.actions';
import { animate, state, style, transition, trigger } from '@angular/animations';

@Component({
    selector: 'dash-gis-feature-report',
    templateUrl: './feature-report.component.html',
    styleUrls: ['./feature-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: IsolatedReportService,
            useFactory: isolatedReportFactory,
            deps: [Injector, [new Optional(), new SkipSelf(), IsolatedReportService]],
        },
    ],
    animations: [
        trigger('shouldDisplay', [
            state(
                'void',
                style({
                    opacity: 0,
                })
            ),
            state(
                'true',
                style({
                    opacity: 1,
                })
            ),
            state(
                'false',
                style({
                    opacity: 0,
                })
            ),
            transition('* => *', [animate('0.2s')]),
        ]),
    ],
})
export class FeatureReportComponent
    extends TypedReportDirective
    implements OnInit, OnChanges, OnDestroy, DoCheck, AfterContentChecked, AfterViewInit
{
    @Output() edit = new EventEmitter<any>();
    @Output() overrideSubtype = new EventEmitter<ReportSubtypeValue>();

    parcelDisplayAddress$: Observable<ParcelDisplayAddress>;
    streetViewStaticUrl$: Observable<string>;

    protected parcelImageLoaded = false;

    @Select(ReportState.parcelImageUrl)
    private parcelImageUrl$: Observable<(id: string, reportName: string) => string>;

    @Select(ReportState.parcelDisplayOptions)
    private streetViewOptions$: Observable<(id: string, reportName: string) => StreetViewMetadata>;

    @Select(ReportState.parcelDisplayAddress)
    private parcelDisplayAddressFunc$: Observable<(id: string, reportName: string) => ParcelDisplayAddress>;

    @ViewChild('parcelImageContainer', { read: ElementRef })
    private parcelImageContainer: ElementRef;

    private reportDataInternal: any[];
    private subTypeInternal: ReportSubtypeValue;
    private resizeObserver: ResizeObserver;

    constructor(
        logger: LoggerService,
        reportService: ReportService,
        isolatedReport: IsolatedReportService,
        cdr: ChangeDetectorRef,
        actions$: Actions,
        store: Store,
        el: ElementRef,
        private digitizeService: DigitizePointService
    ) {
        super(logger, reportService, isolatedReport, cdr, actions$, store, el);
    }

    ngOnInit() {
        super.ngOnInit();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);

        if (changes.launchOptions) {
            const current: ReportLaunchOptions = changes.launchOptions.currentValue;
            const previous: ReportLaunchOptions = changes.launchOptions.previousValue;

            if (current && !isEqual(current, previous)) {
                this.reportChange();

                this.parcelDisplayAddress$ = this.getParcelDisplayAddress$();
                this.streetViewStaticUrl$ = this.getStreetViewStaticUrl$();
            }
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    ngDoCheck(): void {
        super.ngDoCheck();
    }

    ngAfterContentChecked() {
        super.ngAfterContentChecked();

        if (this.parcelImageContainer && !this.resizeObserver) {
            this.initializeResizeObserver();
        }
    }

    ngAfterViewInit(): void {
        this.featureReportKeys$
            .pipe(distinctUntilChanged(isEqual), takeUntil(this.dispose$))
            .subscribe((keys: any[]) => {
                if (keys && keys.length > 0) {
                    this.reportChange();
                }
            });
    }

    onImageLoaded() {
        this.parcelImageLoaded = true;
    }

    setParcelReportImageSize(resizeEntries: ResizeObserverEntry[]) {
        resizeEntries.forEach((entry) => {
            if (entry.contentRect?.width && entry.contentRect?.height) {
                this.store.dispatch(
                    new SetParcelImageSize({
                        id: this.id,
                        reportName: this.definition.name,
                        width: Math.floor(entry.contentRect.width),
                        height: Math.floor(entry.contentRect.height),
                    })
                );
            }
        });
    }

    launchStreetView() {
        const panoIdFn = this.store.selectSnapshot<(id: string, reportName: string) => ParcelDisplayOptions | null>(
            ReportState.parcelDisplayOptions
        );
        const opts = panoIdFn(this.id, this.definition.name);
        if (opts?.pano) {
            const payload: LaunchStreetViewPayload = { pano: opts.pano };
            if (opts?.heading) {
                payload.heading = opts.heading;
            }
            this.store.dispatch(new LaunchStreetView(payload));
        }
    }

    setRecordCount(count: number) {
        super.setRecordCount(count);
        this.updateSubType();
    }

    private getStreetViewStaticUrl$(): Observable<string> {
        return this.parcelImageUrl$.pipe(
            map((fn) => fn(this.isolatedReport.id, this.definition.name)),
            distinctUntilChanged(),
            defaultIfEmpty(''),
            tap((url) => {
                if (!url) {
                    this.parcelImageLoaded = false;
                }
            })
        );
    }

    private getParcelDisplayAddress$(): Observable<ParcelDisplayAddress> {
        return this.parcelDisplayAddressFunc$.pipe(
            map((fn) => fn(this.isolatedReport.id, this.definition.name)),
            distinctUntilChanged(isEqual)
        );
    }

    private initializeResizeObserver() {
        this.resizeObserver = new ResizeObserver(
            throttle((e) => this.setParcelReportImageSize(e), 500, {
                leading: false,
                trailing: true,
            })
        );
        this.resizeObserver.observe(this.parcelImageContainer.nativeElement);
    }

    private reportChange() {
        this.updateSubType();
        this.store.dispatch(
            new ClearParcelDisplayOptions({
                id: this.id,
                reportName: this.definition.name,
            })
        );
    }

    private updateSubType() {
        let subtype: ReportSubtypeValue;
        switch (true) {
            case this.recordCountInternal < 0:
                subtype = 'pending';
                break;
            case this.recordCountInternal === 0:
                subtype = 'empty';
                break;
            case this.definition.subType === 'label':
                subtype = 'label';
                break;
            case this.definition.subType === 'chart':
                subtype = 'chart';
                break;
            case this.recordCountInternal === 1:
                subtype = 'detail';
                break;
            case this.recordCountInternal > 1:
                subtype = 'tabular';
                break;
            default:
                subtype = this.definition.subType;
        }
        this.subTypeInternal = subtype;
        this.overrideSubtype.emit(subtype);
    }

    get reportData() {
        return this.reportDataInternal;
    }

    set reportData(value: any[]) {
        this.reportDataInternal = value;
        this.digitizeService.getLayerKeyField(this.definition.layer).pipe(
            map((keyField) => {
                let key = null;
                if (this.reportData && this.reportData.length === 1) {
                    key = this.reportData[0][keyField];
                }
                this.isolatedReport.setDigitizeKey(key);
            })
        );
    }

    get digitizeKey$() {
        return this.isolatedReport.digitizeKey$;
    }

    get subtype() {
        return this.subTypeInternal;
    }

    get hasStreetView(): boolean {
        return this.subtype === 'detail' && !!this.definition.parcelAddressField;
    }
}
